/**
 * Frame selection — the reference-dependent command that materializes a mech's mounts, and the
 * internal `restore_loadout` that makes its undo/redo lossless without re-reading the compendium.
 *
 * A leaf-handler group of `./leafReducer`'s switch; see `../reducer` for the tier's contract.
 */

import { CommandError, err, ok, type ApplyCtx, type Result } from "@lancer-vtt/shared";
import { MissingContentError } from "@lancer-vtt/compendium";
import type { RosterCommand, RosterLeafCommand } from "../commands";
import { licenseRequirement, meetsLicenseRequirement } from "../licenses";
import { slotSizesForMount } from "../mounts";
import type { LoadoutData, MechData, MountData, RosterState, SlotData } from "../types";
import { changed, requireCompendium, requireMech, requirePilot, withMech, withPilot, type RosterApplyResult } from "./context";

export function applyRestoreLoadout(
  state: RosterState,
  cmd: Extract<RosterLeafCommand, { type: "restore_loadout" }>,
): Result<RosterApplyResult, CommandError> {
  // Internal inverse of select_frame: restore the prior frame id + captured
  // loadout (with weapons) verbatim. Its own inverse is a forward
  // select_frame back to the frame this restores *over* — but select_frame
  // rebuilds an empty layout, so to keep undo↔redo lossless we make the
  // inverse another restore_loadout capturing the current (post-select)
  // frame+loadout. That way redo re-runs the original select_frame's effect
  // exactly without re-reading the compendium.
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  const mechR = requireMech(pilot, cmd.mechId);
  if (!mechR.ok) return mechR;
  const mech = mechR.value;
  const curLoadout = mech.loadouts[cmd.loadoutIndex];
  if (!curLoadout) {
    return err(
      new CommandError(
        `restore_loadout: mech ${cmd.mechId} has no loadout at index ${cmd.loadoutIndex}`,
      ),
    );
  }
  const curFrameId = mech.frameId;
  const nextMech: MechData = {
    ...mech,
    frameId: cmd.frameId,
    loadouts: mech.loadouts.map((l, i) =>
      i === cmd.loadoutIndex ? cmd.loadout : l,
    ),
  };
  return ok({
    state: withPilot(state, withMech(pilot, nextMech)),
    inverse: {
      type: "restore_loadout",
      actorId: cmd.actorId,
      pilotId: cmd.pilotId,
      mechId: cmd.mechId,
      frameId: curFrameId,
      loadoutIndex: cmd.loadoutIndex,
      loadout: curLoadout,
    },
    events: changed(cmd.pilotId),
  });
}

// ── select_frame (reference-dependent: materializes mounts) ─────────────────────

export function applySelectFrame(
  state: RosterState,
  cmd: Extract<RosterLeafCommand, { type: "select_frame" }>,
  ctx: ApplyCtx,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  const mechR = requireMech(pilot, cmd.mechId);
  if (!mechR.ok) return mechR;
  const mech = mechR.value;
  const compendiumR = requireCompendium(ctx);
  if (!compendiumR.ok) return compendiumR;
  const compendium = compendiumR.value;

  const frame = compendium.getById("frame", cmd.frameId);
  if (!frame) {
    // Missing referenced content is a distinct, structured error (carries the
    // type/id/file); it stays a `throw` — `Result`'s error channel is typed to
    // `CommandError`, and the callers/tests that care assert `.toThrow(MissingContentError)`.
    throw new MissingContentError("frame", cmd.frameId, `mech ${cmd.mechId}`);
  }

  // License gate (the single-source `licenses.ts` helper, mirrored by the Phase-3
  // kit): hard-reject selecting a frame whose required license rank the pilot hasn't
  // earned. A GMS frame passes via the seeded GMS rank; an unlicensed/rank-0 frame is
  // always allowed. Reads the requirement off the compendium frame, like the size-fit
  // gate reads the weapon's mount size.
  if (!meetsLicenseRequirement(pilot, frame, "frame")) {
    const req = licenseRequirement(frame, "frame");
    return err(
      new CommandError(
        `select_frame: frame ${cmd.frameId} requires license ${req?.id} rank ${req?.level}; pilot has not earned it`,
      ),
    );
  }

  // Materialize the active loadout's mount/slot layout from the frame's mounts.
  // Changing frame clears weapons: the mount layout differs between frames, so
  // weapon indices wouldn't carry meaning across the change (and a previously-fit
  // weapon may not fit the new layout). It also clears systems + mods (a mod dies
  // with its weapon, and a system loadout is frame-scoped — COMP/CON re-fits the
  // whole loadout to a new frame). The prior loadout is captured whole (systems +
  // mods included) for a lossless inverse via `restore_loadout`.
  const mountStrings = (frame as { mounts?: string[] }).mounts ?? [];
  const mounts: MountData[] = [];
  for (const mtype of mountStrings) {
    const sizes = slotSizesForMount(mtype);
    if (!sizes) {
      return err(
        new CommandError(
          `select_frame: frame ${cmd.frameId} has unknown mount type "${mtype}"`,
        ),
      );
    }
    const slots: SlotData[] = sizes.map(() => ({ weaponId: null, modId: null }));
    mounts.push({ type: mtype, slots });
  }

  const activeIdx = mech.activeLoadoutIndex;
  const prevLoadout = mech.loadouts[activeIdx];
  if (!prevLoadout) {
    return err(
      new CommandError(
        `select_frame: mech ${cmd.mechId} has no active loadout at index ${activeIdx}`,
      ),
    );
  }
  const prevFrameId = mech.frameId;

  // Clear systems too (mods are already gone — the mounts above are rebuilt fresh
  // with empty, mod-less slots). The whole prior loadout is captured in the inverse,
  // so `restore_loadout` brings systems + mods back verbatim on undo.
  const newLoadout: LoadoutData = { ...prevLoadout, mounts, systems: [] };
  const nextMech: MechData = {
    ...mech,
    frameId: cmd.frameId,
    loadouts: mech.loadouts.map((l, i) => (i === activeIdx ? newLoadout : l)),
  };
  const nextPilot = withMech(pilot, nextMech);

  // Inverse: restore the prior frame id AND the prior loadout (with its weapons)
  // verbatim. `select_frame` alone can't do that (it rebuilds an empty layout),
  // so the inverse is an internal `restore_loadout` that splices the captured
  // frame+loadout back. Its own inverse is the forward select_frame.
  const inverse: RosterCommand = {
    type: "restore_loadout",
    actorId: cmd.actorId,
    pilotId: cmd.pilotId,
    mechId: cmd.mechId,
    frameId: prevFrameId,
    loadoutIndex: activeIdx,
    loadout: prevLoadout,
  };

  return ok({
    state: withPilot(state, nextPilot),
    inverse,
    events: changed(cmd.pilotId),
  });
}
